import { useField } from 'formik';
import React from 'react';
import Text from '@service-victoria/component-library/components/Text';
import {
  isVictorianReturningFromRedZone,
  isFullVaxERZVictorian
} from 'steps/Assess/travel-reasons';
import { showTransitInvalidWarning } from 'steps/Assess/selectors';
import Checkbox from '@service-victoria/component-library/components/Checkbox';
import * as Interfaces from 'interfaces';
import { Address } from 'Components/Address';
import { countryCodes } from 'interfaces/common';
import * as Styled from '../../../styled';
import { Props as AutoCompleteProps } from '@service-victoria/component-library/components/AutoComplete';
import type { DeepPartial } from 'types/globals';
import { isCrossBorderResident } from '../../travel-reasons';

type HomeAddressProps = {
  setDeltaValues: (values: DeepPartial<Pick<Interfaces.Common.FormValues, 'assess'>>) => void;
  isInternationalArrivalPermit: boolean;
};

export const HomeAddress: React.FC<HomeAddressProps> = ({
  setDeltaValues,
  isInternationalArrivalPermit
}) => {
  const [{ value: reasonCode }] = useField<string>('assess.reasons');
  const [{ value: returningVicInvalidHome }] = useField<boolean>(
    'assess.Depart.returningVicInvalidHome'
  );
  const [{ value: homeAddressCountry }] = useField<countryCodes>(
    'assess.Depart.homeAddressCountry'
  );
  const [{ value: homeAddressIsDestination }] = useField<boolean>(
    'assess.Depart.homeAddressIsDestination'
  );
  const [{ value: homeState }] = useField<string>('assess.Depart.homeAddress.fullDetails.state');

  const onSelection = (
    address: Interfaces.Common.AddressWithLga,
    selectedValue: AutoCompleteProps.OptionType
  ) => {
    setDeltaValues({
      assess: {
        Depart: {
          homeAddress: {
            summaryData: selectedValue,
            fullDetails: {
              id: address.id,
              suburb: address.suburbOrPlaceOrLocality || address.stateOrTerritory,
              country: address.country,
              lga: address.lga || address.regionalCouncilCode,
              state: address.stateOrTerritory,
              postCode: address.postCode,
              fullAddress: address.addressLine1
            }
          },
          returningVicInvalidHome:
            (isVictorianReturningFromRedZone(reasonCode) || isFullVaxERZVictorian(reasonCode)) &&
            address.stateOrTerritory !== 'VIC'
        }
      }
    });
  };

  return (
    <>
      <Styled.Spacer24 />
      <Text.Heading.H3 fontWeight='bold'>Home Address</Text.Heading.H3>
      <Styled.Spacer24 />
      <Address
        fieldName='assess.Depart.homeAddress'
        label='Address'
        onSelection={onSelection}
        country={homeAddressCountry}
        addressType={isInternationalArrivalPermit ? 'both' : 'local'}
      />
      <Styled.Spacer24 />
      {!isCrossBorderResident(reasonCode) && (
        <Checkbox
          label='Home address is my main destination'
          name='assess.Depart.homeAddressIsDestination'
          data-testid='HomeAddress-CheckBox'
          errorLabelProps={{
            'data-testid': 'HomeAddress-CheckBoxError'
          }}
        />
      )}
      <Styled.Spacer24 />
      {returningVicInvalidHome && (
        <Styled.NotificationInfo
          value='To apply for this permit type, you must be a Victorian resident. Your home address must be in Victoria.'
          data-testid='VictoriaHomeResident-Notification'
        />
      )}
      {homeAddressIsDestination && showTransitInvalidWarning(reasonCode, homeState) && (
        <Styled.NotificationInfo
          value='This address is in Victoria. Your main destination must not be in Victoria.'
          aria-invalid='true'
          aria-labelledby='assess.Depart.homeAddress.summaryData.label'
          data-testid='TransitHomeDestInvalid-Notification'
        />
      )}
    </>
  );
};
